<template>
  <div class="locatie-access-requests-page">
    <AppHeader
      title="Locatieverzoeken"
      :show-back-button="true"
      back-to="/"
      back-text="Terug naar dashboard"
    />

    <div class="locatie-access-requests-content">
      <div v-if="loading" class="state-card">Verzoeken laden...</div>

      <div v-else-if="errorMessage" class="state-card state-card--error">
        {{ errorMessage }}
      </div>

      <div v-else-if="selectedRequest" class="request-card">
        <div class="request-card__header">
          <div>
            <h2>{{ locatieLabel }}</h2>
            <p>{{ ibedrijfLabel }} vraagt toegang tot deze locatie.</p>
          </div>
          <Tag :severity="statusSeverity" :value="statusLabel" />
        </div>

        <div class="request-card__details">
          <div>
            <span class="request-card__label">Aangevraagd op</span>
            <span>{{ formatDate(selectedRequest.created_at) }}</span>
          </div>
          <div>
            <span class="request-card__label">Gevraagde rollen</span>
            <div class="request-card__chips">
              <Tag v-if="selectedRequest.requested_is_eigenaar" severity="success" value="Eigenaar" />
              <Tag v-if="selectedRequest.requested_is_beheerder" severity="info" value="Beheerder" />
              <Tag v-if="selectedRequest.requested_is_default" severity="warn" value="Standaard" />
              <span
                v-if="!selectedRequest.requested_is_eigenaar && !selectedRequest.requested_is_beheerder"
                class="text-background-500"
              >
                Geen rollen aangevraagd
              </span>
            </div>
          </div>
          <div v-if="selectedRequest.denial_reason">
            <span class="request-card__label">Reden afwijzing</span>
            <span>{{ selectedRequest.denial_reason }}</span>
          </div>
        </div>

        <Message v-if="actionMessage" :severity="actionMessageSeverity" :closable="false">
          {{ actionMessage }}
        </Message>

        <div v-if="selectedRequest.status === 'pending'" class="request-card__actions">
          <Textarea
            v-model="denialReason"
            rows="3"
            autoResize
            placeholder="Optionele reden bij afwijzen"
          />
          <div class="request-card__buttons">
            <Button
              label="Afwijzen"
              severity="danger"
              outlined
              :loading="denying"
              :disabled="approving"
              @click="denyRequest"
            />
            <Button
              label="Goedkeuren"
              :loading="approving"
              :disabled="denying"
              @click="approveRequest"
            />
          </div>
        </div>
      </div>

      <div v-else class="state-card">Geen locatieverzoek gevonden.</div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import AppHeader from '../components/AppHeader.vue'
  import { supabase } from '../lib/supabase'
  import type { Tables } from '../../../../types'
  import Button from 'primevue/button'
  import Message from 'primevue/message'
  import Tag from 'primevue/tag'
  import Textarea from 'primevue/textarea'

  type LocatieAccessRequestRow = Tables<'locatie_access_requests'>
  type LocatieRow = Tables<'locaties'>
  type IbedrijfRow = Tables<'ibedrijven'>

  const route = useRoute()
  const loading = ref(false)
  const approving = ref(false)
  const denying = ref(false)
  const errorMessage = ref('')
  const actionMessage = ref('')
  const actionMessageSeverity = ref<'success' | 'info' | 'warn' | 'error'>('info')
  const denialReason = ref('')
  const selectedRequest = ref<LocatieAccessRequestRow | null>(null)
  const locatie = ref<Tables<'locaties'> | null>(null)
  const ibedrijf = ref<Tables<'ibedrijven'> | null>(null)

  const requestId = computed(() => (typeof route.query.request === 'string' ? route.query.request : ''))

  const locatieLabel = computed(() => {
    if (!locatie.value) return 'Locatie'
    return (
      locatie.value.naam ||
      [locatie.value.straat, locatie.value.huisnr, locatie.value.plaats].filter(Boolean).join(' ') ||
      'Locatie'
    )
  })

  const ibedrijfLabel = computed(() => {
    return ibedrijf.value?.contactnaam || 'Een inspectiebedrijf'
  })

  const statusLabel = computed(() => {
    switch (selectedRequest.value?.status) {
      case 'approved':
        return 'Goedgekeurd'
      case 'denied':
        return 'Afgewezen'
      case 'withdrawn':
        return 'Ingetrokken'
      default:
        return 'Openstaand'
    }
  })

  const statusSeverity = computed(() => {
    switch (selectedRequest.value?.status) {
      case 'approved':
        return 'success'
      case 'denied':
        return 'danger'
      case 'withdrawn':
        return 'contrast'
      default:
        return 'warn'
    }
  })

  function formatDate(value: string) {
    return new Date(value).toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  async function loadRequest() {
    if (!requestId.value) {
      errorMessage.value = 'Geen verzoek-ID ontvangen.'
      return
    }

    loading.value = true
    errorMessage.value = ''

    try {
      const { data, error: requestError } = await supabase.rpc('get_locatie_access_request_details_for_opdrachtgever', {
        p_request_id: requestId.value,
      })

      if (requestError) {
        throw requestError
      }

      const response = data as {
        success?: boolean
        error?: string
        request?: LocatieAccessRequestRow
        locatie?: LocatieRow | null
        ibedrijf?: IbedrijfRow | null
      } | null

      if (!response?.success) {
        if (response?.error === 'request_not_found') {
          selectedRequest.value = null
          locatie.value = null
          ibedrijf.value = null
          return
        }

        throw new Error(response?.error || 'Kon locatieverzoek niet laden.')
      }

      selectedRequest.value = response.request || null

      if (!selectedRequest.value) {
        return
      }

      locatie.value = response.locatie || null
      ibedrijf.value = response.ibedrijf || null
    } catch (error) {
      console.error('Failed to load locatie access request:', error)
      errorMessage.value = error instanceof Error ? error.message : 'Kon locatieverzoek niet laden.'
    } finally {
      loading.value = false
    }
  }

  async function approveRequest() {
    if (!selectedRequest.value) return

    approving.value = true
    actionMessage.value = ''

    try {
      const { data, error } = await supabase.rpc('approve_locatie_access_request', {
        p_request_id: selectedRequest.value.id,
      })

      if (error) {
        throw error
      }

      const response = data as { success?: boolean; error?: string } | null
      if (!response?.success) {
        throw new Error(response?.error || 'Kon verzoek niet goedkeuren.')
      }

      actionMessageSeverity.value = 'success'
      actionMessage.value = 'Het locatieverzoek is goedgekeurd.'
      denialReason.value = ''
      if (selectedRequest.value) {
        selectedRequest.value = {
          ...selectedRequest.value,
          status: 'approved',
          reviewed_at: new Date().toISOString(),
        }
      }
    } catch (error) {
      console.error('Failed to approve locatie access request:', error)
      actionMessageSeverity.value = 'error'
      actionMessage.value = error instanceof Error ? error.message : 'Kon verzoek niet goedkeuren.'
    } finally {
      approving.value = false
    }
  }

  async function denyRequest() {
    if (!selectedRequest.value) return

    denying.value = true
    actionMessage.value = ''

    try {
      const { data, error } = await supabase.rpc('deny_locatie_access_request', {
        p_request_id: selectedRequest.value.id,
        p_reason: denialReason.value.trim() || undefined,
      })

      if (error) {
        throw error
      }

      const response = data as { success?: boolean; error?: string } | null
      if (!response?.success) {
        throw new Error(response?.error || 'Kon verzoek niet afwijzen.')
      }

      actionMessageSeverity.value = 'info'
      actionMessage.value = 'Het locatieverzoek is afgewezen.'
      await loadRequest()
    } catch (error) {
      console.error('Failed to deny locatie access request:', error)
      actionMessageSeverity.value = 'error'
      actionMessage.value = error instanceof Error ? error.message : 'Kon verzoek niet afwijzen.'
    } finally {
      denying.value = false
    }
  }

  onMounted(() => {
    loadRequest()
  })
</script>

<style scoped>
  .locatie-access-requests-page {
    max-width: 64rem;
    margin: 0 auto;
    padding: 2rem;
  }

  .locatie-access-requests-content {
    display: grid;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .state-card,
  .request-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.75rem;
    padding: 1.25rem;
  }

  .state-card--error {
    color: var(--p-red-600);
  }

  .request-card {
    display: grid;
    gap: 1rem;
  }

  .request-card__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .request-card__header h2 {
    margin: 0;
    font-size: 1.25rem;
  }

  .request-card__header p {
    margin: 0.25rem 0 0;
    color: var(--p-text-muted-color);
  }

  .request-card__details {
    display: grid;
    gap: 1rem;
  }

  .request-card__details > div {
    display: grid;
    gap: 0.35rem;
  }

  .request-card__label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--p-text-muted-color);
  }

  .request-card__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .request-card__actions {
    display: grid;
    gap: 0.75rem;
  }

  .request-card__buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }

  @media (max-width: 640px) {
    .locatie-access-requests-page {
      padding: 1rem;
    }

    .request-card__header,
    .request-card__buttons {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>
